/**
 * OnboardingGuard Component
 *
 * Ensures users have completed onboarding before accessing protected routes.
 * - Shows loading state while fetching onboarding status
 * - Redirects to /onboarding if profile is incomplete
 * - Renders children when onboarding is complete
 *
 * Owner decision 2026-10-08 (blocking first-run gate): it wraps the shell, so
 * while it redirects no navigation or page renders, on any route. A user who
 * already uses BeHeard (sessions, but no onboarding profile) is NOT blocked,
 * and neither is anyone whose state cannot be read (profile or session history
 * failed): never lock anyone out on an error. /onboarding stays reachable.
 *
 * Owner decision 2026-10-09 (onboarding pop-up): a new user no longer goes to
 * a full-page /onboarding. The real shell renders behind OnboardingModal,
 * inert and aria-hidden, on the home route (/dashboard: read-only queries, safe
 * with no data); a deep link is held at /dashboard and restored when the user
 * opens BeHeard from the "You're all set" step. Same audience as the gate.
 *
 * Note: This guard should be used INSIDE AuthGuard, not as a replacement.
 */

import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { OnboardingModal } from '../onboarding/OnboardingModal';
import { useGetOnboardingProfileQuery } from '../../store/api/onboardingApi';
import { useGetMyGamificationOverviewQuery } from '../../store/api/gamificationApi';
import { onboardingHistory } from '../../utils/onboardingWelcome';

interface OnboardingGuardProps {
  children?: React.ReactNode;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { data, isLoading, isFetching, isError } = useGetOnboardingProfileQuery();

  // Check if onboarding is complete
  // User needs both a profile AND to have completed the onboarding flow
  const hasCompletedOnboarding = data?.hasCompletedOnboarding ?? false;
  const hasProfile = data?.profile !== null && data?.profile !== undefined;
  const notOnboarded = !isLoading && !isError && (!hasCompletedOnboarding || !hasProfile);

  // Existing BeHeard data (sessions) marks a historical user. Read only for a
  // user without a profile — the same read (and cache entry) as the Welcome's.
  const history = onboardingHistory(useGetMyGamificationOverviewQuery(undefined, { skip: !notOnboarded }));
  const location = useLocation();
  const navigate = useNavigate();

  // Once open, the pop-up stays mounted through the save and the profile
  // re-read that follows it, until "Open BeHeard" closes it.
  const [modalOpen, setModalOpen] = useState(false);
  const [closed, setClosed] = useState(false);
  const wantsModal = !closed && !isError && notOnboarded && history === 'none';
  const showModal = modalOpen || wantsModal;
  useEffect(() => {
    if (wantsModal && !modalOpen) setModalOpen(true);
  }, [wantsModal, modalOpen]);

  // Show loading state while fetching onboarding status (and, for a user
  // without a profile, their session history; and the profile re-read that
  // follows the wizard's submit, so a finished user is not sent back to it).
  if (!showModal && !closed && (isLoading || (notOnboarded && (history === 'loading' || (history === 'none' && isFetching))))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Only a NEW user (no profile, and the server says 0 sessions) gets the
  // pop-up; sessions > 0 or an unreadable history let them through.
  if (showModal) {
    const returnTo = (location.state as { onboardingReturnTo?: string } | null)?.onboardingReturnTo;
    if (location.pathname !== '/dashboard') {
      return (
        <Navigate
          to="/dashboard"
          replace
          state={{ onboardingReturnTo: `${location.pathname}${location.search}${location.hash}` }}
        />
      );
    }
    return (
      <>
        <div {...{ inert: '' }} aria-hidden="true" data-testid="onboarding-app-behind">
          {children ?? <Outlet />}
        </div>
        <OnboardingModal
          onFinished={() => {
            setClosed(true);
            setModalOpen(false);
            if (returnTo && returnTo !== '/' && returnTo !== '/dashboard') navigate(returnTo, { replace: true });
          }}
        />
      </>
    );
  }

  // If there's an error, allow access rather than blocking forever
  // The actual pages can handle showing appropriate error states
  if (isError) {
    return <>{children ?? <Outlet />}</>;
  }

  // Onboarding complete (or a historical / unknown user) - render children
  return <>{children ?? <Outlet />}</>;
}
